import Text from '@ingka/text';
import Loading, { LoadingBall } from '@ingka/loading';
import Tabs, { Tab, TabPanel } from '@ingka/tabs';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { IntegrityCodesSchema } from '@lotta/shared/types-lottery-schemas';
import { memo, useEffect, useState, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InlineMessage from '@ingka/inline-message';
import styles from '../../LotteryForm.module.scss';
import { ControlledTextArea } from '../../components/controlled-text-area/ControlledTextArea';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { mapRecord } from '../../utils';
import { ControlledLegalTermsCheckboxes } from '../../components/controlled-legal-terms-checkboxes/ControlledLegalTermsCheckboxes';

export const MODULE_NAME = 'legal';

export type LegalTermsFormType = Static<typeof LegalTermsFormSchema>;
export const LegalTermsFormSchema = Type.Object(
  {
    termsAndConditions: Type.Optional(
      Type.String({
        format: 'uri',
        maxLength: 1000,
        pattern: 'https://www.ikea.com/',
        errorMessage: {
          format: 'form-error-message-uri',
          pattern: 'form-error-message-uri',
          maxLength: 'form-lottery-name-error-message-max-length',
        },
      })
    ),
    contestRules: Type.Optional(
      Type.String({
        format: 'uri',
        maxLength: 1000,
        pattern: 'https://www.ikea.com/',
        errorMessage: {
          format: 'form-error-message-uri',
          pattern: 'form-error-message-uri',
          maxLength: 'form-lottery-name-error-message-max-length',
        },
      })
    ),
    privacyPolicy: Type.Optional(
      Type.String({
        format: 'uri',
        maxLength: 1000,
        pattern: 'https://www.ikea.com/',
        errorMessage: {
          format: 'form-error-message-uri',
          pattern: 'form-error-message-uri',
          maxLength: 'form-lottery-name-error-message-max-length',
        },
      })
    ),
    disclaimerText: Type.Optional(
      Type.String({
        maxLength: 500,
        errorMessage: {
          maxLength: 'form-lottery-name-error-message-max-length',
        },
      })
    ),
  },

  {
    additionalProperties: false,
    $id: 'LegalTermsFormSchema',
  }
);

export type LegalFormType = Static<typeof LegalFormSchema>;
export const LegalFormSchema = Type.Object(
  {
    translation: Type.Record(
      Type.String(), // language code
      LegalTermsFormSchema
    ),
    integrityCodes: Type.Array(IntegrityCodesSchema, {
      uniqueItems: true,
    }),
  },
  { additionalProperties: false, $id: 'LegalFormTranslationSchema' }
);

export const LegalFormTransformer: ModuleTransformer<LegalFormType> = {
  fromPayload: ({ translation, lottery }) => {
    return {
      translation: mapRecord(translation, (translation) => ({
        termsAndConditions: translation.legal?.termsAndConditions,
        contestRules: translation.legal?.contestRules,
        privacyPolicy: translation.legal?.privacyPolicy,
        disclaimerText: translation.legal?.disclaimerText,
      })),
      integrityCodes: lottery?.integrityCodes ?? defaultValues.integrityCodes,
    };
  },
  toPayload: (formData) => {
    return {
      translation: mapRecord(formData.translation, (translation) => ({
        legal: {
          termsAndConditions: translation.termsAndConditions,
          contestRules: translation.contestRules,
          privacyPolicy: translation.privacyPolicy,
          disclaimerText: translation.disclaimerText || undefined,
        },
      })),
      lottery: {
        integrityCodes: formData.integrityCodes.filter((code) =>
          Object.values(formData.translation).every(
            (translation) => translation[code] !== undefined
          )
        ),
      },
    };
  },
};

const defaultValues: LegalFormType = {
  translation: {},
  integrityCodes: [],
};

export type LegalFormProps = FormModuleProps<LegalFormType>;

export const LegalForm: React.FC<LegalFormProps> = memo(({ values, onChange, onValidation }) => {
  const { t } = useTranslation();
  const { retailUnit, language } = useLocale();
  const {
    data: retailUnitData,
    error: retailUnitError,
    isLoading: retailUnitIsLoading,
  } = useGetRetailUnitQuery({ retailUnit });

  const {
    control,
    formState: { isValid, errors },
    setValue,
    getValues,
  } = useFormModule({
    moduleName: MODULE_NAME,
    defaultValues: values || defaultValues,
    schema: LegalFormSchema,
    onChange,
    onValidation,
    mode: 'onChange',
  });

  // Watch dynamically integrityCodes property to be able to render the matching legal terms
  const integrityCodesFromForm = useWatch({ control, name: 'integrityCodes' });
  // Watch dynamically translation property to be able to reset the coresponding value
  const translationFromForm = useWatch({ control, name: 'translation' });

  const translationLanguages = useMemo(() => {
    return Object.keys(translationFromForm);
  }, [translationFromForm]);

  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showContestRules, setShowContestRules] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const integrityCodesFromRetailUnit = retailUnitData?.retailUnit?.integrityCodes;

  const handleUpdate = (id: string, checked: boolean) => {
    switch (id) {
      case 'termsAndConditions':
        setShowTermsAndConditions(checked);
        if (!checked) {
          // setting fields to the coresponding language to undefined to enable proper validation of optional fields
          translationLanguages.forEach((language) => {
            setValue(`translation.${language}.termsAndConditions`, undefined);
            control.unregister(`translation.${language}.termsAndConditions`);
          });
        } else {
          translationLanguages.forEach((language) => {
            setValue(`translation.${language}.termsAndConditions`, '');
          });
        }
        break;
      case 'contestRules':
        setShowContestRules(checked);
        if (!checked) {
          // setting fields to the coresponding language to undefined to enable proper validation of optional fields
          translationLanguages.forEach((language) => {
            setValue(`translation.${language}.contestRules`, undefined);
            control.unregister(`translation.${language}.contestRules`);
          });
        } else {
          translationLanguages.forEach((language) => {
            setValue(`translation.${language}.contestRules`, '');
          });
        }
        break;
      case 'privacyPolicy':
        setShowPrivacyPolicy(checked);
        if (!checked) {
          // setting fields to the coresponding language to undefined to enable proper validation of optional fields
          translationLanguages.forEach((language) => {
            setValue(`translation.${language}.privacyPolicy`, undefined);
            control.unregister(`translation.${language}.privacyPolicy`);
          });
        } else {
          translationLanguages.forEach((language) => {
            setValue(`translation.${language}.privacyPolicy`, '');
          });
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (
      integrityCodesFromForm?.includes('termsAndConditions') ||
      integrityCodesFromRetailUnit?.includes('termsAndConditions')
    ) {
      setShowTermsAndConditions(true);
      retailUnitData?.retailUnit.languages.forEach((language) => {
        if (getValues(`translation.${language}.termsAndConditions`) === undefined) {
          setValue(`translation.${language}.termsAndConditions`, '');
        }
      });
    } else {
      setShowTermsAndConditions(false);
    }

    if (
      integrityCodesFromForm?.includes('contestRules') ||
      integrityCodesFromRetailUnit?.includes('contestRules')
    ) {
      setShowContestRules(true);
      retailUnitData?.retailUnit.languages.forEach((language) => {
        if (getValues(`translation.${language}.contestRules`) === undefined) {
          setValue(`translation.${language}.contestRules`, '');
        }
      });
    } else {
      setShowContestRules(false);
    }

    if (
      integrityCodesFromForm?.includes('privacyPolicy') ||
      integrityCodesFromRetailUnit?.includes('privacyPolicy')
    ) {
      setShowPrivacyPolicy(true);
      retailUnitData?.retailUnit.languages.forEach((language) => {
        if (getValues(`translation.${language}.privacyPolicy`) === undefined) {
          setValue(`translation.${language}.privacyPolicy`, '');
        }
      });
    } else {
      setShowPrivacyPolicy(false);
    }
  }, [
    integrityCodesFromForm,
    integrityCodesFromRetailUnit,
    translationLanguages,
    setValue,
    getValues,
    retailUnitData?.retailUnit.languages,
  ]);

  if (retailUnitIsLoading) {
    return (
      <Loading text={`${t('general-fetching')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (retailUnitError || !retailUnitData) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const languageNames = new Intl.DisplayNames([language], { type: 'language' });
  const tabsAndPanels = retailUnitData.retailUnit.languages.map((language) => {
    const id = `legal-tab-${language}`;
    return {
      id,
      tab: <Tab key={language} text={languageNames.of(language)} tabPanelId={id} type="button" />,
      panel: (
        <TabPanel key={language} tabPanelId={id}>
          {showTermsAndConditions && (
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-config-terms-and-conditions-label"
                name={`translation.${language}.termsAndConditions`}
                showCharacterLimit={false}
                upperLimit={LegalTermsFormSchema.properties.termsAndConditions.maxLength}
              />
            </div>
          )}
          {showContestRules && (
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-config-contest-rules-label"
                name={`translation.${language}.contestRules`}
                showCharacterLimit={false}
                upperLimit={LegalTermsFormSchema.properties.contestRules.maxLength}
              />
            </div>
          )}
          {showPrivacyPolicy && (
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-privacy-policy-config-label"
                name={`translation.${language}.privacyPolicy`}
                showCharacterLimit={false}
                upperLimit={LegalTermsFormSchema.properties.privacyPolicy.maxLength}
              />
            </div>
          )}
          <div className={styles['form-elements']}>
            <ControlledTextArea
              control={control}
              labelKey="form-sign-up-config-disclaimer-text-label"
              name={`translation.${language}.disclaimerText`}
              showCharacterLimit={false}
              upperLimit={LegalTermsFormSchema.properties.disclaimerText.maxLength}
            />
          </div>
        </TabPanel>
      ),
    };
  });

  const tabs = tabsAndPanels.map(({ tab }) => tab);
  const panels = tabsAndPanels.map(({ panel }) => panel);

  return (
    <>
      <InlineMessage
        className={styles['inline-message']}
        variant="informative"
        title={`${t('form-explanation-message-legal-title')}`}
        body={`${t('form-explanation-message-legal-body')}`}
      />
      {!isValid && (
        <InlineMessage
          className={styles['inline-message']}
          variant="cautionary"
          title={`${t('form-missing-translations-title')}`}
          body={`${t('form-missing-translations-body')}`}
        />
      )}
      <form>
        <div className={styles['form-elements']}>
          <ControlledLegalTermsCheckboxes
            control={control}
            errorMessage={errors.integrityCodes?.message as string}
            integrityCodes={integrityCodesFromRetailUnit}
            onUpdate={handleUpdate}
          />
        </div>
        <Tabs tabs={tabs} tabPanels={panels} defaultActiveTab={tabsAndPanels[0].id} />
      </form>
    </>
  );
});

export const module = {
  name: MODULE_NAME,
  transformer: LegalFormTransformer,
  form: LegalForm,
} as const;
